import React, { ChangeEvent, useEffect, useRef, useState } from 'react';

import globalStyles from 'assets/stylesheets/global-styles.module.scss';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { Breakpoints } from 'common/enums';
import { FileUploader } from 'common/file-uploader';
import { ImagePreview } from 'common/image-preview';
import { Input } from 'common/input';
import { Line } from 'common/line';
import { Modal } from 'common/modal';
import { Spinner } from 'common/spinner';
import {
  vaultCoverSupportedExtensions,
  mbVaultCoverMaxSize,
} from 'config/constants';
import { classnames, noEmptyFields } from 'helpers/utils';
import { useHandleApiError } from 'hooks/use-handle-api-error';
import { useMediaQuery } from 'hooks/use-media-query';
import { Vault } from 'models/vault';
import { VaultEditModal } from 'models/vaultEditModal';
import { VaultsController } from 'networking/controllers/vaults-controller';

const mandatoryFields: MandatoryFieldsType = {
  title: '',
};

type ModalEditVaultProps = {
  vault: Vault;
  onClose: () => void;
  onSuccess?: () => void;
};

const ModalEditVault: React.FC<ModalEditVaultProps> = ({
  vault,
  onClose,
  onSuccess,
}) => {
  const [formState, setFormState] = useState<VaultEditModal>(
    new VaultEditModal(vault),
  );
  const [errors, setErrors] = useState<MandatoryFieldsType>(mandatoryFields);
  const [backError, setBackError] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState<boolean>(false);

  const isMobileOrTablet = useMediaQuery(`(max-width: ${Breakpoints.md}px)`);

  const coverId = useRef<string | null | undefined>(undefined);
  const setApiError = useHandleApiError(() => {
    setBackError(true);
    setFetching(false);
  });

  useEffect(
    () => () => {
      setFetching(false);
    },
    [],
  );

  const handleImageUpload = ({ url, signedId: idImage }: FileResponseType) => {
    coverId.current = idImage;

    setIsLoadingImage(false);

    setFormState((prevState) => ({
      ...prevState,
      imageUrl: url,
    }));
  };

  const handleRemovePreview = () => {
    coverId.current = null;

    setFormState((prevState) => ({
      ...prevState,
      imageUrl: '',
    }));
  };

  const validateTitleField = () => {
    const emptyFieldMessage =
      formState.title.trim() === '' ? 'name shouldn’t be empty' : '';
    setErrors((prevState) => ({
      ...prevState,
      title: emptyFieldMessage.toUpperCase(),
    }));
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormState((prevUserState) => ({ ...prevUserState, [name]: value }));
  };

  const submitModal = async () => {
    setFetching(true);
    const { id, title } = formState;

    const requestData: VaultEditRequestType = { vault: { id, title } };
    requestData.vault.coverSignedId = coverId.current;

    try {
      setFetching(true);
      await VaultsController.editVaultModal(vault.id, requestData);
      onSuccess!();
    } catch (err: any) {
      setApiError(err);
    }
  };

  const showModalContentView = () => {
    const { title, imageUrl } = formState;

    return (
      <Modal
        title="Edit vault name & cover photo"
        onClose={onClose}
        contentClassName="w-[600px] bg-white!"
      >
        <div className="text__body__regular__medium__gray800 flex flex-col gap-4">
          <div>
            {!imageUrl ? (
              <FileUploader
                className="h-[330px]"
                supportedExtensions={vaultCoverSupportedExtensions}
                maxFileSizeInMb={mbVaultCoverMaxSize}
                uploadingMessage="Uploading cover..."
                onSuccess={(file: FileResponseType) => handleImageUpload(file)}
                onLoading={() => setIsLoadingImage(true)}
                onError={() => setIsLoadingImage(false)}
                onCancel={() => setIsLoadingImage(false)}
              />
            ) : (
              <ImagePreview
                imageLocation={imageUrl}
                removePreview={() => handleRemovePreview()}
              />
            )}
          </div>

          <Line className="mb-0" />

          <div>
            <Input
              id="title"
              name="title"
              errorMessage={errors.title}
              label="Vault name"
              placeholder="Name"
              value={title}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                handleChange(e);
              }}
              onBlur={() => {
                validateTitleField();
              }}
            />
          </div>

          <div className={classnames(backError ? 'visible' : 'invisible')}>
            <p className="text__body__semi__bold__medium__warning30">
              Something went wrong.
            </p>
            <p className="text__body__regular__tiny__gray700">
              We couldn’t save your changes. Please try again.
            </p>
          </div>
        </div>

        <div className="flex flex-row gap-2.5 self-end">
          <Button
            onClick={onClose}
            buttonStyle={ButtonStyle.GreyGhost}
            buttonSize={ButtonSize.Small}
          >
            Cancel
          </Button>
          {!fetching ? (
            <Button
              buttonSize={ButtonSize.Small}
              disabled={!noEmptyFields({ title }) || isLoadingImage}
              buttonStyle={ButtonStyle.Primary}
              onClick={() => submitModal()}
            >
              {isMobileOrTablet ? 'Save' : 'Save changes'}
            </Button>
          ) : (
            <div className="flex h-9 w-[125px] items-center justify-center">
              <Spinner className={globalStyles.smallSpinner} />
            </div>
          )}
        </div>
      </Modal>
    );
  };

  return showModalContentView();
};

export { ModalEditVault };
